/** Escolha da loja no login: campo de busca com lista.
 *
 * O `<select>` nativo cortava o nome ("A∴R∴G∴B∴O∴L∴S∴ Lauro Müller II - nº 16…")
 * e, com mais lojas na plataforma, obrigava a rolar uma lista sem busca. Aqui
 * se digita parte do nome ou o número — sem se preocupar com acento — e a
 * lista mostra o nome inteiro, com brasão e número para não haver dúvida
 * entre lojas de nome parecido.
 *
 * Teclado: ↑ ↓ andam, Enter escolhe, Esc fecha e devolve a loja de antes.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { LojaPublica } from '../types';
import { normalizar } from '../utils/formato';
import { Icone } from './Icone';

function detalhe(loja: LojaPublica): string {
  return [
    loja.numero && !loja.nome.includes(loja.numero) ? `nº ${loja.numero}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function SeletorDeLoja({
  lojas,
  valor,
  aoEscolher,
  desabilitado,
}: {
  lojas: LojaPublica[];
  valor: string;
  aoEscolher: (slug: string) => void;
  desabilitado?: boolean;
}) {
  const id = useId();
  const caixa = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [destaque, setDestaque] = useState(0);

  const escolhida = lojas.find((l) => l.slug === valor);

  const filtradas = useMemo(() => {
    const termo = normalizar(busca);
    if (!termo) return lojas;
    return lojas.filter((l) =>
      normalizar(`${l.nome} ${l.nome_curto ?? ''} ${l.numero ?? ''} ${l.oriente ?? ''} ${l.uf ?? ''}`)
        .includes(termo),
    );
  }, [busca, lojas]);

  // Clique fora fecha sem mudar nada.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (!caixa.current?.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  // A lista reabre com a loja atual em destaque, não com a primeira.
  const abrir = () => {
    if (desabilitado) return;
    setBusca('');
    setDestaque(Math.max(0, lojas.findIndex((l) => l.slug === valor)));
    setAberto(true);
  };

  const escolher = (loja: LojaPublica) => {
    aoEscolher(loja.slug);
    setAberto(false);
    setBusca('');
    campo.current?.blur();
  };

  const teclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!aberto && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      abrir();
      return;
    }
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestaque((d) => Math.min(d + 1, filtradas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const loja = filtradas[destaque];
      if (loja) escolher(loja);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAberto(false);
      setBusca('');
    }
  };

  // Mantém o item destacado à vista quando se anda com as setas.
  useEffect(() => {
    if (!aberto) return;
    document.getElementById(`${id}-op-${destaque}`)?.scrollIntoView({ block: 'nearest' });
  }, [destaque, aberto, id]);

  return (
    <div className={`seletor-loja${aberto ? ' aberto' : ''}`} ref={caixa}>
      <div className="seletor-loja-campo" onClick={abrir}>
        <Icone nome={aberto ? 'busca' : 'banco'} tamanho={17} className="seletor-loja-icone" />
        <input
          ref={campo}
          className="login-input"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={`${id}-lista`}
          aria-autocomplete="list"
          aria-label="Loja"
          aria-activedescendant={aberto ? `${id}-op-${destaque}` : undefined}
          placeholder={aberto ? 'Buscar por nome ou número…' : 'Escolha a loja'}
          value={aberto ? busca : (escolhida?.nome ?? '')}
          onChange={(e) => {
            setBusca(e.target.value);
            setDestaque(0);
            if (!aberto) setAberto(true);
          }}
          onFocus={abrir}
          onKeyDown={teclar}
          disabled={desabilitado}
          autoComplete="off"
          spellCheck={false}
          title={escolhida?.nome}
        />
        <Icone nome="baixo" tamanho={16} className="seletor-loja-seta" />
      </div>

      {aberto && (
        <ul className="seletor-loja-lista" id={`${id}-lista`} role="listbox">
          {filtradas.length === 0 ? (
            <li className="seletor-loja-vazio">Nenhuma loja encontrada para “{busca}”.</li>
          ) : (
            filtradas.map((loja, i) => (
              <li
                key={loja.slug}
                id={`${id}-op-${i}`}
                role="option"
                aria-selected={loja.slug === valor}
                className={`seletor-loja-op${i === destaque ? ' destaque' : ''}${
                  loja.slug === valor ? ' escolhida' : ''
                }`}
                onMouseEnter={() => setDestaque(i)}
                // `mousedown`, não `click`: o clique chegaria depois do blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolher(loja);
                }}
              >
                <span className="seletor-loja-brasao">
                  {loja.brasao_url ? (
                    <img src={loja.brasao_url} alt="" />
                  ) : (
                    <Icone nome="marca" tamanho={16} />
                  )}
                </span>
                <span className="seletor-loja-texto">
                  <strong>{loja.nome}</strong>
                  {detalhe(loja) && <span>{detalhe(loja)}</span>}
                </span>
                {loja.slug === valor && <Icone nome="check" tamanho={16} className="seletor-loja-check" />}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
